import { IMAGE_INFO_MAP, IMAGE_KIND, IMAGES_ORDER } from './images.const';
import type { VehicleAdditionalImage, VehicleImage } from './vehicle.schema';

interface Presets {
	desktop: string;
	mobile: string;
	thumbnail: string;
	thumbnailTiny: string;
}

const IMAGE_WITH_NO_KIND = 'error_no_provided_kind';

export const mapVehicleImage = (image: VehicleImage) => ({
	...image,
	category: IMAGE_INFO_MAP[image.kind ?? IMAGE_WITH_NO_KIND]?.type,
	kind: image.kind ?? IMAGE_WITH_NO_KIND,
	presets: image.presets.reduce((acc, it) => ({ ...acc, [it.name]: it.url }), {} as Presets),
	presetsArray: image.presets,
	title: image.kind ? IMAGE_INFO_MAP[image.kind]?.description : IMAGE_WITH_NO_KIND,
});

export type MappedVehicleImage = ReturnType<typeof mapVehicleImage>;

const sortVehicleImages = (a: VehicleImage, b: VehicleImage) => {
	const kindComparison =
		IMAGES_ORDER.indexOf(a.kind ?? IMAGE_WITH_NO_KIND) - IMAGES_ORDER.indexOf(b.kind ?? IMAGE_WITH_NO_KIND);
	if (kindComparison !== 0) {
		return kindComparison;
	}
	return a.id - b.id;
};

const isVehicleImageIncluded = (image: VehicleImage) => IMAGES_ORDER.includes(image?.kind ?? IMAGE_WITH_NO_KIND);

const appendQS = (url: string, value: string) => {
	const separator = url.includes('?') ? '&' : '?';
	return `${url}${separator}${value}`;
};

const mapAdditionalImage = (
	{ createdAt, croppedUrl, originalUrl, redactedUrl }: VehicleAdditionalImage,
	index: number,
	enquiryId: number,
): MappedVehicleImage => {
	const url = redactedUrl ?? croppedUrl ?? originalUrl ?? '';
	return mapVehicleImage({
		createdAt,
		damageMeta: null,
		enquiryId,
		id: index,
		isDamaged: null,
		kind: IMAGE_KIND.additionalImage,
		lastUpdated: createdAt,
		presets: [
			{
				name: 'thumbnail',
				url: appendQS(url, 'w=248&h=186'),
			},
			{
				name: 'desktop',
				url: appendQS(url, 'w=512&h=384'),
			},
			{
				name: 'mobile',
				url: appendQS(url, 'w=432&h=324'),
			},
			{
				name: 'thumbnailTiny',
				url: appendQS(url, 'w=64&h=48'),
			},
		],
		status: 'sellerSupplied',
		url,
	});
};

export const mapImages = (
	enquiryId: number,
	vehicleImages: VehicleImage[],
	additionalImages?: VehicleAdditionalImage[],
) => {
	const images = vehicleImages.filter(isVehicleImageIncluded).sort(sortVehicleImages).map(mapVehicleImage);

	additionalImages
		?.filter((image) => image.redactedUrl || image.croppedUrl || image.originalUrl)
		.forEach((image, index) => images.push(mapAdditionalImage(image, index, enquiryId)));

	const categories = images.reduce(
		(acc, it) => ({ ...acc, [it.category]: (acc[it.category] || 0) + 1 }),
		{} as Record<string, number>,
	);

	return { categories, images };
};
